import { useQuery } from '@tanstack/react-query'
import type { CardIconKey } from '@chillout/ui'
import { supabase } from '../../../lib/supabase'

export interface StampMilestoneIcon {
  stampPosition: number
  cardIcon: CardIconKey
}

export interface StampSettings {
  earningRule: {
    method: 'spend_based' | 'per_meal'
    amountPerStamp: number
  }
  stampTarget: number
  maxStampsPerTransaction: number
  receiptReferenceEnabled: boolean
  completionCardIcon: CardIconKey
  milestones: StampMilestoneIcon[]
}

async function fetchStampSettings(): Promise<StampSettings> {
  const [settingsResult, configResult] = await Promise.all([
    supabase
      .from('app_settings')
      .select('max_stamps_per_transaction, receipt_reference_enabled')
      .single(),
    supabase
      .from('loyalty_config')
      .select('earning_method, amount_per_stamp, stamp_target, completion_card_icon, milestones')
      .eq('status', 'active')
      .single(),
  ])

  if (settingsResult.error) throw settingsResult.error
  if (configResult.error) throw configResult.error

  const settings = settingsResult.data
  const config = configResult.data

  return {
    earningRule: {
      method: config.earning_method as StampSettings['earningRule']['method'],
      amountPerStamp: config.amount_per_stamp ?? 0,
    },
    stampTarget: config.stamp_target,
    maxStampsPerTransaction: settings.max_stamps_per_transaction,
    receiptReferenceEnabled: settings.receipt_reference_enabled,
    completionCardIcon: config.completion_card_icon as CardIconKey,
    milestones: config.milestones as StampMilestoneIcon[],
  }
}

const SETTINGS_POLL_INTERVAL = 5 * 60 * 1000

export function useStampSettings() {
  return useQuery<StampSettings>({
    queryKey: ['stamp-settings'],
    queryFn: fetchStampSettings,
    staleTime: SETTINGS_POLL_INTERVAL,
    refetchInterval: SETTINGS_POLL_INTERVAL,
  })
}